import imageCompression from 'browser-image-compression';
import supabase from './supabaseClient';

// Sanitize filename function
export const sanitizeFileName = (name) => {
  // First, replace common Spanish characters with ASCII equivalents
  const replacements = {
    'á': 'a', 'à': 'a', 'ä': 'a', 'â': 'a', 'ā': 'a', 'ã': 'a',
    'é': 'e', 'è': 'e', 'ë': 'e', 'ê': 'e', 'ē': 'e',
    'í': 'i', 'ì': 'i', 'ï': 'i', 'î': 'i', 'ī': 'i',
    'ó': 'o', 'ò': 'o', 'ö': 'o', 'ô': 'o', 'ō': 'o', 'õ': 'o',
    'ú': 'u', 'ù': 'u', 'ü': 'u', 'û': 'u', 'ū': 'u',
    'ñ': 'n',
    'ç': 'c',
    'Á': 'A', 'À': 'A', 'Ä': 'A', 'Â': 'A', 'Ā': 'A', 'Ã': 'A',
    'É': 'E', 'È': 'E', 'Ë': 'E', 'Ê': 'E', 'Ē': 'E',
    'Í': 'I', 'Ì': 'I', 'Ï': 'I', 'Î': 'I', 'Ī': 'I',
    'Ó': 'O', 'Ò': 'O', 'Ö': 'O', 'Ô': 'O', 'Ō': 'O', 'Õ': 'O',
    'Ú': 'U', 'Ù': 'U', 'Ü': 'U', 'Û': 'U', 'Ū': 'U',
    'Ñ': 'N',
    'Ç': 'C'
  };
  
  // Replace accented characters
  let sanitized = name;
  Object.keys(replacements).forEach(char => {
    sanitized = sanitized.replace(new RegExp(char, 'g'), replacements[char]);
  });
  
  // Remove any remaining non-ASCII characters and keep only safe ones
  sanitized = sanitized.replace(/[^a-zA-Z0-9\-_\.]/g, '_')
                      .replace(/\.+/g, '.') // Collapse multiple dots
                      .replace(/^\.+|\.+$/g, '') // Remove leading/trailing dots
                      .replace(/_+/g, '_') // Collapse multiple underscores
                      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  
  return sanitized;
};

// Default compression options
const DEFAULT_COMPRESSION_OPTIONS = {
  maxSizeMB: 1, // Maximum file size in MB
  maxWidthOrHeight: 512, // Maximum width or height in pixels
  useWebWorker: true, // Use web worker for faster compression
};

// Upload image to Supabase storage with compression
export const uploadImageToStorage = async (file, folder = 'thumbnails', compressionOptions = {}) => {
  try {
    // Compress the image before uploading
    const options = { ...DEFAULT_COMPRESSION_OPTIONS, ...compressionOptions };
    const compressedFile = await imageCompression(file, options);

    // Sanitize the filename to ensure compatibility with Supabase Storage
    const sanitizedFileName = sanitizeFileName(compressedFile.name);
    const filePath = `${folder}/${Date.now()}_${sanitizedFileName}`;
    
    const { error: uploadError } = await supabase.storage
      .from('productsamples')
      .upload(filePath, compressedFile, { upsert: false });

    if (uploadError) {
      throw uploadError;
    }

    // Get the public URL
    const { data } = supabase.storage.from('productsamples').getPublicUrl(filePath);
    
    return {
      success: true,
      url: data.publicUrl,
      filePath: filePath
    };
    
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error al procesar la imagen'
    };
  }
};

// Save thumbnail to familia_thumbnail table
export const saveFamiliaThumbnail = async (familiaId, imageUrl) => {
  try {
    const { error } = await supabase
      .from('familia_thumbnail')
      .insert({
        familia_id: familiaId,
        url: imageUrl
      });

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error al guardar en la base de datos'
    };
  }
};

// Complete image upload workflow for familia thumbnails
export const uploadFamiliaThumbnail = async (file, familiaId) => {
  try {
    // Upload to storage
    const uploadResult = await uploadImageToStorage(file, 'thumbnails');
    
    if (!uploadResult.success) {
      return uploadResult;
    }

    // Save to database
    const saveResult = await saveFamiliaThumbnail(familiaId, uploadResult.url);
    
    if (!saveResult.success) {
      // If database save fails, we might want to clean up the uploaded file
      // For now, we'll just return the error
      return saveResult;
    }

    return {
      success: true,
      url: uploadResult.url,
      message: 'Imagen subida correctamente'
    };
    
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error inesperado al subir la imagen'
    };
  }
};

// Complete image upload workflow for subfamilia thumbnails
// This is simpler as it just uploads to storage without saving to a specific table
export const uploadSubfamiliaThumbnail = async (file) => {
  try {
    // Upload to storage
    const uploadResult = await uploadImageToStorage(file, 'thumbnails');
    
    if (!uploadResult.success) {
      return uploadResult;
    }

    return {
      success: true,
      url: uploadResult.url,
      filePath: uploadResult.filePath,
      fileName: uploadResult.filePath.split('/').pop(),
      message: 'Imagen subida correctamente'
    };
    
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error inesperado al subir la imagen'
    };
  }
};